/**
 * CopilotSidebar - Replaces the default left nav when on /copilot/<threadId>.
 *
 * The sidebar is now focused on a single job: showing the artifacts the
 * Copilot has produced in *this* session, so the user can pivot between them.
 * Cross-session navigation lives in the chat panel's tab bar (top of the
 * conversation column), which is the right place for it because that's
 * where users come to start, switch, or save chats.
 *
 *  ┌──────────────────────────┐
 *  │  ‹  Back to Copilot   📌 │  ← compact header (back link + findings pin)
 *  ├──────────────────────────┤
 *  │  IN THIS SESSION       n │
 *  │  ▸ artifact 1            │
 *  │  ▸ artifact 2 (active)   │
 *  │  ▸ artifact 3            │
 *  └──────────────────────────┘
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  ButtonBase,
} from '@mui/material';
import { AppDialog } from '../../../components/dialog';
import { useNavigate } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import PushPinRounded from '@mui/icons-material/PushPinRounded';
import { useCopilotStore } from '../stores/useCopilotStore';
import type { WorkspaceArtifact, Finding } from '../stores/useCopilotStore';
import { formatRelativeTime } from '../../../utils/date';

interface CopilotSidebarProps {
  collapsed: boolean;
}

export function CopilotSidebar({ collapsed }: CopilotSidebarProps) {
  const navigate = useNavigate();
  const [keyNumbersOpen, setKeyNumbersOpen] = React.useState(false);

  const artifacts = useCopilotStore((s: any) => s.workspaceTabs) as WorkspaceArtifact[];
  const activeTabId = useCopilotStore((s: any) => s.activeTabId) as string | null;
  const setActiveTab = useCopilotStore((s: any) => s.setActiveTab) as (id: string | null) => void;
  const findings = useCopilotStore((s: any) => s.findings) as Finding[];
  const seenArtifactIds = useCopilotStore((s: any) => s.seenArtifactIds) as Set<string>;

  // ── Entrance animation for new artifacts ────────────────────────────────
  const isFirstRender = React.useRef(true);
  const prevArtifactIdsRef = React.useRef<Set<string>>(new Set(artifacts.map((a) => a.id)));
  const [newArtifactIds, setNewArtifactIds] = React.useState<Set<string>>(new Set());
  const artifactListRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevArtifactIdsRef.current = new Set(artifacts.map((a) => a.id));
      return;
    }
    const currentIds = new Set(artifacts.map((a) => a.id));
    const prevIds = prevArtifactIdsRef.current;
    const freshIds = new Set<string>();
    currentIds.forEach((id) => {
      if (!prevIds.has(id) && !seenArtifactIds.has(id)) freshIds.add(id);
    });
    if (freshIds.size > 0) {
      setNewArtifactIds(freshIds);
      setTimeout(() => artifactListRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 50);
      const timer = setTimeout(() => setNewArtifactIds(new Set()), 2500);
      prevArtifactIdsRef.current = currentIds;
      return () => clearTimeout(timer);
    }
    prevArtifactIdsRef.current = currentIds;
  }, [artifacts, seenArtifactIds]);

  // ── Collapsed state: icon strip ──────────────────────────────────────────
  if (collapsed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1, gap: 1 }}>
        <Tooltip title="Back to Copilot Home" placement="right">
          <IconButton
            size="small"
            onClick={() => navigate('/copilot')}
            sx={(t) => ({ color: t.tokens.text.secondary, '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.sidebar.itemHoverBg } })}
          >
            <HomeRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {findings.length > 0 && (
          <Tooltip title={`${findings.length} key number${findings.length === 1 ? '' : 's'}`} placement="right">
            <IconButton
              size="small"
              onClick={() => setKeyNumbersOpen(true)}
              sx={(t) => ({
                color: t.tokens.semantic.warning,
                bgcolor: t.tokens.semantic.warningSoft,
                '&:hover': { bgcolor: t.tokens.semantic.warningSoft, filter: 'brightness(0.96)' },
              })}
            >
              <PushPinRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <KeyNumbersModal open={keyNumbersOpen} onClose={() => setKeyNumbersOpen(false)} findings={findings} />
      </Box>
    );
  }

  // ── Expanded: header + In-this-session list ──────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* ─── Header: single back affordance + findings pin ────────────────── */}
      <Box
        sx={(t) => ({
          px: 1,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          flexShrink: 0,
        })}
      >
        <ButtonBase
          onClick={() => navigate('/copilot')}
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.625,
            flex: 1,
            minWidth: 0,
            px: 1,
            height: 30,
            borderRadius: `${t.ds.radius.sm}px`,
            color: t.tokens.text.secondary,
            transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
            justifyContent: 'flex-start',
            '&:hover': {
              color: t.tokens.text.primary,
              bgcolor: t.tokens.sidebar.itemHoverBg,
            },
            '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing, outline: 'none' },
          })}
        >
          <ArrowBackRounded sx={{ fontSize: 16, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              letterSpacing: '-0.005em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Copilot Home
          </Typography>
        </ButtonBase>
        {findings.length > 0 && (
          <Tooltip title="View key numbers" placement="bottom">
            <ButtonBase
              onClick={() => setKeyNumbersOpen(true)}
              sx={(t) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.875,
                height: 24,
                flexShrink: 0,
                borderRadius: `${t.ds.radius.full}px`,
                bgcolor: t.tokens.semantic.warningSoft,
                color: t.tokens.semantic.warning,
                transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                '&:hover': { bgcolor: t.tokens.semantic.warningSoft, filter: 'brightness(0.96)' },
                '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing },
              })}
            >
              <PushPinRounded sx={{ fontSize: 12 }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1, letterSpacing: '0.005em' }}>
                {findings.length}
              </Typography>
            </ButtonBase>
          </Tooltip>
        )}
      </Box>

      <KeyNumbersModal open={keyNumbersOpen} onClose={() => setKeyNumbersOpen(false)} findings={findings} />

      {/* ─── In this session (artifacts) ──────────────────────────────────── */}
      <Box sx={{ px: 2, pt: 1.5, pb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
          In this session
        </Typography>
        {artifacts.length > 0 && (
          <Typography sx={(t) => ({
            fontSize: '0.75rem',
            fontWeight: 600,
            color: t.tokens.text.tertiary,
            ml: 'auto',
            fontVariantNumeric: 'tabular-nums',
          })}>
            {artifacts.length}
          </Typography>
        )}
      </Box>
      <Box ref={artifactListRef} sx={{ flex: 1, overflow: 'auto', px: 1, pb: 1, minHeight: 0 }}>
        {artifacts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography sx={(t) => ({ fontSize: '0.75rem', color: t.tokens.text.tertiary, lineHeight: 1.5 })}>
              No results yet.<br />
              Ask Copilot a question to start.
            </Typography>
          </Box>
        ) : (
          [...artifacts].reverse().map((artifact) => (
            <ArtifactRow
              key={artifact.id}
              artifact={artifact}
              isActive={artifact.id === activeTabId}
              isNew={newArtifactIds.has(artifact.id)}
              isUnseen={!seenArtifactIds.has(artifact.id)}
              onSelect={() => setActiveTab(artifact.id)}
            />
          ))
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// Artifact Row - single item in the artifact list
// ============================================================================

const ARTIFACT_ICONS: Record<string, React.ElementType> = {
  query_result: TableChartOutlined,
  chart: BarChartRounded,
  metric_card: SpeedRounded,
  insight: ArticleOutlined,
  finding: PushPinRounded,
};

function ArtifactRow({
  artifact,
  isActive,
  isNew,
  isUnseen,
  onSelect,
}: {
  artifact: WorkspaceArtifact;
  isActive: boolean;
  isNew: boolean;
  isUnseen: boolean;
  onSelect: () => void;
}) {
  const Icon = ARTIFACT_ICONS[artifact.type] || TableChartOutlined;

  const timeLabel = artifact.createdAt ? formatRelativeTime(artifact.createdAt) : '';

  let subtitle: string;
  if (artifact.type === 'query_result') {
    const rowCountLabel = (artifact.rowCount ?? 0).toLocaleString();
    subtitle = `${artifact.connectorName ? artifact.connectorName + ' · ' : ''}${rowCountLabel} rows · ${timeLabel}`;
  } else if (artifact.type === 'chart') {
    const rowCountLabel = (artifact.rowCount ?? 0).toLocaleString();
    subtitle = `${artifact.chartType} · ${rowCountLabel} rows · ${timeLabel}`;
  } else if (artifact.type === 'metric_card') {
    subtitle = `${artifact.metricName} · ${timeLabel}`;
  } else {
    subtitle = timeLabel;
  }

  return (
    <ButtonBase
      onClick={onSelect}
      sx={(t) => ({
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.25,
        py: 1,
        borderRadius: `${t.ds.radius.sm}px`,
        justifyContent: 'flex-start',
        textAlign: 'left',
        bgcolor: isActive
          ? t.tokens.brand.primarySoft
          : isNew
            ? t.tokens.brand.primarySofter
            : 'transparent',
        transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, top: 4, bottom: 4,
          width: 2,
          borderRadius: 1,
          bgcolor: isActive ? t.tokens.brand.primary : 'transparent',
          transition: `background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        },
        ...(isNew && {
          animation: 'artifactSlideIn 0.5s cubic-bezier(0.22,1,0.36,1), artifactHighlight 2.5s ease-out',
          '@keyframes artifactSlideIn': {
            '0%': { opacity: 0, transform: 'translateX(-12px)' },
            '100%': { opacity: 1, transform: 'translateX(0)' },
          },
          '@keyframes artifactHighlight': {
            '0%':   { boxShadow: t.tokens.shadow.focusRing },
            '100%': { boxShadow: 'inset 0 0 0 0 transparent' },
          },
        }),
        '&:hover': {
          bgcolor: isActive ? t.tokens.brand.primarySoft : t.tokens.sidebar.itemHoverBg,
        },
        '&:focus-visible': { boxShadow: t.tokens.shadow.focusRing, outline: 'none' },
      })}
    >
      {isUnseen && !isActive && (
        <Box
          sx={(t) => ({
            position: 'absolute',
            top: 8,
            right: 8,
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: t.tokens.brand.primary,
            animation: 'unreadPulse 2s ease-in-out infinite',
            '@keyframes unreadPulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%':      { opacity: 0.45, transform: 'scale(0.7)' },
            },
          })}
        />
      )}
      <Box
        sx={(t) => ({
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: `${t.ds.radius.xs}px`,
          bgcolor: isActive ? t.tokens.brand.primarySoft : t.tokens.surface.sunken,
          color: isActive ? t.tokens.brand.primary : t.tokens.text.tertiary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 0.125,
        })}
      >
        <Icon sx={{ fontSize: 14 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={(t) => ({
            fontSize: '0.8125rem',
            fontWeight: isActive ? 600 : 500,
            letterSpacing: '-0.005em',
            color: isActive ? t.tokens.text.primary : t.tokens.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            lineHeight: 1.35,
          })}
        >
          {artifact.title}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: '0.75rem',
            color: t.tokens.text.tertiary,
            display: 'block',
            lineHeight: 1.4,
            mt: 0.125,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          })}
        >
          {subtitle}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

// ============================================================================
// Key Numbers Modal
// ============================================================================

function KeyNumbersModal({
  open,
  onClose,
  findings,
}: {
  open: boolean;
  onClose: () => void;
  findings: Finding[];
}) {
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="xs"
      icon={
        <Box
          sx={(t) => ({
            width: 24, height: 24,
            borderRadius: `${t.ds.radius.xs}px`,
            bgcolor: t.tokens.semantic.warningSoft,
            color: t.tokens.semantic.warning,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          })}
        >
          <PushPinRounded sx={{ fontSize: 14 }} />
        </Box>
      }
      title="Key numbers"
      headerExtras={
        <Typography
          sx={(t) => ({ fontSize: '0.75rem', color: t.tokens.text.tertiary })}
        >
          Session only
        </Typography>
      }
      dividers
    >
        {findings.length === 0 ? (
          <Typography
            sx={(t) => ({
              py: 4,
              textAlign: 'center',
              fontSize: '0.875rem',
              color: t.tokens.text.tertiary,
            })}
          >
            No key numbers yet
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {findings.map((f, i) => (
              <Box
                key={f.id || i}
                sx={(t) => ({
                  py: 1.5,
                  borderBottom: i < findings.length - 1 ? `1px solid ${t.tokens.border.subtle}` : 'none',
                })}
              >
                <Typography
                  sx={(t) => ({
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: t.tokens.text.primary,
                    lineHeight: 1.4,
                  })}
                >
                  {f.title}
                </Typography>
                {f.narrative && (
                  <Typography
                    sx={(t) => ({
                      fontSize: '0.8125rem',
                      color: t.tokens.text.secondary,
                      lineHeight: 1.5,
                      mt: 0.5,
                    })}
                  >
                    {f.narrative}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
    </AppDialog>
  );
}

export default CopilotSidebar;
